import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  MenuItem,
  PrimaryButton,
  TextField,
  Typography,
} from '@bb-ui/react-library/dist/components';
import { makeStyles, Theme } from '@bb-ui/react-library';
import { Refresh } from '@bb-ui/icons/dist/medium';
import { Status, TenantType } from '@lct-jobs/core';
import { PageTemplate } from 'components/PageTemplate';
import { ExecutionTable } from 'components/JobExecutionList/ExecutionTable';
import {
  MassOperationType,
  getMassOperationName,
  getMassOperationType,
} from 'components/JobExecution/massOperationType';
import { useAuthorization } from 'hooks/useAuthorization';
import { TenantIndex, useTenantIndex } from 'hooks/useTenantIndex';
import { AuthorizationPermission, AuthorizationService } from 'hooks/useAuthorization.types';
import { useJobCatalog } from './useJobCatalog';
import { useAppConfigContext } from 'contexts/AppConfigContext';
import { PAGE_LIMIT, SourcedExecution, useExecutions } from './useExecutions';

const useStyles = makeStyles((theme: Theme) => ({
  filtersContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  select: {
    minWidth: 160,
    backgroundColor: theme.palette.background.paper,
  },
  searchField: {
    flexGrow: 1,
  },
  count: {
    marginBottom: theme.spacing(1),
  },
}));

/** Days to look back. The region list allows up to 365. */
export const LOOKBACK_DAYS = [7, 30, 90, 365];
const DEFAULT_LOOKBACK_DAYS = 30;

const STATUSES = Object.values(Status) as string[];

const TENANT_TYPES = (Object.values(TenantType) as string[]).filter((type) => type !== 'Untyped');

/**
 * Whether an execution is a run of a job: the job itself, or a mass job that ran it on many
 * sites (whose own jobId is the mass job executor's).
 */
const isRunOfJob = (execution: SourcedExecution, jobId: string) =>
  execution.jobId === jobId ||
  (getMassOperationType(execution) === MassOperationType.MASS_JOB &&
    getMassOperationName(execution) === jobId);

/**
 * With a job chosen, pages keep loading until a page of its runs is in, but at most this many
 * times per choice. The region list already returns a job's runs across its whole lookback, so
 * the extra loads mostly page through the Global and Regional lists, which cannot filter; Load
 * more continues after.
 */
const MAX_JOB_AUTO_LOADS = 3;

const ALL = 'all';
/** Region choice listing every region */
const ALL_REGIONS = 'allRegions';

/**
 * A site's name from the tenant list, which the app loads once and shares, so naming rows costs
 * no request per row. Tenants missing from the list keep showing their ID.
 */
const tenantName = (index: TenantIndex | undefined, tenantId?: string) => {
  const tenant = tenantId ? index?.getTenant(tenantId) : undefined;
  return tenant?.displayName || tenant?.name || undefined;
};

const matchesSearch = (execution: SourcedExecution, query: string, name?: string) =>
  [
    execution.jobName,
    execution.jobId,
    execution.executionId,
    execution.user,
    execution.tenantId,
    name,
  ]
    .filter(Boolean)
    .some((field) => String(field).toLowerCase().includes(query));

type HasPermission = (
  service: AuthorizationService,
  permission: AuthorizationPermission,
) => boolean;

/**
 * The detail page of a row, or null when the viewer lacks the access that page's route needs.
 * Every Regional execution (mass job, batch run or mass query) opens in the Regional execution
 * view, which lives under query tooling; single-site runs open in the tenant's executions.
 */
const detailPath = (execution: SourcedExecution, hasPermission: HasPermission): string | null => {
  const region = execution.sourceRegion;
  switch (execution.source) {
    case 'global':
      return `/jobs/executions/${execution.executionId}`;
    case 'regional':
      return hasPermission('query-tooling', 'access')
        ? `/query-tooling/regions/${region}/executions/${execution.executionId}`
        : null;
    default:
      return hasPermission('tenant', 'read')
        ? `/tenants/${execution.tenantId}/executions/${execution.executionId}`
        : null;
  }
};

/**
 * Executions across Houston, like the Captain Jobs list: the global and regional executions of a
 * region, where mass jobs, batch runs and mass queries start, and every job run on a single site
 * from the lct-jobs region list.
 */
export const Executions: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const env = useAppConfigContext();
  const regions = env.houstonSupportedRegions ?? ['us-east-1'];
  const { hasPermission } = useAuthorization();
  const { index: tenantIndex } = useTenantIndex();

  const [region, setRegion] = React.useState(regions[0]);
  const [days, setDays] = React.useState(DEFAULT_LOOKBACK_DAYS);
  const [status, setStatus] = React.useState<string>(ALL);
  const [job, setJob] = React.useState<string>(ALL);
  const [tenantType, setTenantType] = React.useState<string>(ALL);
  const [search, setSearch] = React.useState('');

  const regionsKey = regions.join(',');
  const selectedRegions = React.useMemo(
    () => (region === ALL_REGIONS ? regionsKey.split(',') : [region]),
    [region, regionsKey],
  );
  const { executions, loading, error, hasMore, loadMore, refresh } = useExecutions(
    selectedRegions,
    days,
    job === ALL ? undefined : job,
    tenantType === ALL ? undefined : tenantType,
  );
  // Jobs are registered in every region, so one region's catalog serves All regions
  const jobCatalog = useJobCatalog(selectedRegions[0]);

  const visible = React.useMemo(() => {
    const query = search.trim().toLowerCase();
    return executions.filter(
      (execution) =>
        (status === ALL || execution.status === status) &&
        (tenantType === ALL || execution.tenantType === tenantType) &&
        (job === ALL || isRunOfJob(execution, job)) &&
        (!query || matchesSearch(execution, query, tenantName(tenantIndex, execution.tenantId))),
    );
  }, [executions, status, tenantType, job, search, tenantIndex]);

  // Matches for a chosen job can sit far down the list, so load pages until a page of them is in
  const jobAutoLoadsRef = React.useRef(0);
  React.useEffect(() => {
    jobAutoLoadsRef.current = 0;
  }, [job, region, days, tenantType]);
  React.useEffect(() => {
    if (
      job !== ALL &&
      !loading &&
      hasMore &&
      visible.length < PAGE_LIMIT &&
      jobAutoLoadsRef.current < MAX_JOB_AUTO_LOADS
    ) {
      jobAutoLoadsRef.current += 1;
      loadMore();
    }
  }, [job, loading, hasMore, visible.length, loadMore]);

  return (
    <PageTemplate
      title={t('executions.pageTitle')}
      data-testid="executions-list-page"
      headerControl={
        <PrimaryButton
          startIcon={<Refresh />}
          onClick={refresh}
          data-testid="executions-refresh-button"
        >
          {t('global.refresh')}
        </PrimaryButton>
      }
    >
      <div className={classes.filtersContainer}>
        <TextField
          select
          id="executions-region-select"
          label={t('executions.region')}
          value={region}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRegion(e.target.value)}
          className={classes.select}
          floatingLabel
        >
          {regions.length > 1 && (
            <MenuItem value={ALL_REGIONS}>{t('executions.allRegions')}</MenuItem>
          )}
          {regions.map((name) => (
            <MenuItem key={name} value={name}>
              {name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          id="executions-lookback-select"
          label={t('executions.lookback')}
          value={days}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDays(Number(e.target.value))}
          className={classes.select}
          floatingLabel
        >
          {LOOKBACK_DAYS.map((option) => (
            <MenuItem key={option} value={option}>
              {t('executions.lookbackDays', { count: option })}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          id="executions-status-select"
          label={t('executions.status')}
          value={status}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStatus(e.target.value)}
          className={classes.select}
          floatingLabel
        >
          <MenuItem value={ALL}>{t('executions.all')}</MenuItem>
          {STATUSES.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          id="executions-tenant-type-select"
          label={t('executions.tenantType')}
          value={tenantType}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTenantType(e.target.value)}
          className={classes.select}
          floatingLabel
        >
          <MenuItem value={ALL}>{t('executions.all')}</MenuItem>
          {TENANT_TYPES.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          id="executions-job-select"
          label={t('executions.job')}
          value={job}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setJob(e.target.value)}
          className={classes.select}
          floatingLabel
        >
          <MenuItem value={ALL}>{t('executions.all')}</MenuItem>
          {jobCatalog.map(({ jobId, jobName }) => (
            <MenuItem key={jobId} value={jobId}>
              {jobName}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          id="executions-search"
          label={t('executions.search')}
          value={search}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
          className={classes.searchField}
          floatingLabel
          helperText={t('executions.searchHelperText')}
        />
      </div>

      {!(loading && executions.length === 0) && (
        <Typography
          variant="body2"
          className={classes.count}
          aria-live="polite"
          data-testid="executions-count"
        >
          {hasMore
            ? t('executions.countPartial', { count: visible.length })
            : t('executions.count', { count: visible.length })}
          {/* Global and Regional cannot filter by job on the server, so a rare job's older runs
              may still be further back once the automatic loads are done */}
          {hasMore && job !== ALL && !loading && (
            <span data-testid="executions-job-hint"> {t('executions.jobMoreMayLoad')}</span>
          )}
        </Typography>
      )}

      <ExecutionTable
        executions={visible}
        loading={loading}
        error={error}
        // The table only checks whether another page exists
        nextPageToken={hasMore ? 'more' : undefined}
        onLoadMore={loadMore}
        emptyMessage={t('jobService.noData')}
        testIdPrefix="executions"
        showOperationType
        showTenant
        tenantNameOf={(execution) => tenantName(tenantIndex, execution.tenantId)}
        showRegion={region === ALL_REGIONS}
        showUser
        detailPathBuilder={(execution) => detailPath(execution as SourcedExecution, hasPermission)}
      />
    </PageTemplate>
  );
};

export default Executions;
